import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Skeleton } from '@/components/ui/skeleton'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { getProviderInboxFn } from '@/lib/firebase/functions'
import { useMyTaxProfile } from '../hooks/useMyTaxProfile'
import { useMyInvoices } from '../hooks/useMyInvoices'
import { taxProfileService } from '../services/taxProfile.service'
import { W9Wizard } from '../components/W9Wizard'
import { EngagementInvoiceList, type EngagementListItem } from '../components/EngagementInvoiceList'
import { SubmitInvoiceDialog } from '../components/SubmitInvoiceDialog'
import { MyInvoicesList } from '../components/MyInvoicesList'
import type { ProviderInvoiceDocument } from '@/types'

interface DialogTarget {
  engagement: { id: string; title: string; clientName: string }
  invoice?: ProviderInvoiceDocument & { id: string }
}

export function ProviderInvoicesPage() {
  const { user, loading: authLoading } = useAuth()
  const memberId = user?.uid ?? null
  const {
    profile,
    loading: profileLoading,
    error: profileError,
    hasTaxIdOnFile,
    refresh: refreshProfile,
  } = useMyTaxProfile(memberId)
  const myInvoices = useMyInvoices(memberId)

  const [wizardOpen, setWizardOpen] = useState(false)
  const [assignments, setAssignments] = useState<EngagementListItem[]>([])
  const [providerMemberId, setProviderMemberId] = useState<string | null>(null)
  const [assignmentsLoading, setAssignmentsLoading] = useState(true)
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null)
  const [dialogTarget, setDialogTarget] = useState<DialogTarget | null>(null)

  const loadAssignments = useCallback(async () => {
    if (!user) return
    setAssignmentsLoading(true)
    setAssignmentsError(null)
    try {
      const { data } = await getProviderInboxFn({})
      setAssignments(data.assignments)
      setProviderMemberId(data.member.id)
    } catch (err) {
      setAssignmentsError(formatUserFacingError(err, 'Failed to load engagements.'))
    } finally {
      setAssignmentsLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (authLoading) return
    if (!user) return
    void loadAssignments()
  }, [user, authLoading, loadAssignments])

  if (authLoading) return null
  if (!user) {
    return <Navigate to="/sign-in" replace state={{ returnTo: '/portal/invoices' }} />
  }

  const unifiedLoading = profileLoading || assignmentsLoading || myInvoices.loading
  const loadError = assignmentsError ?? profileError ?? myInvoices.error

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 space-y-8">
      <header className="space-y-1">
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Provider Portal</p>
        <h1 className="text-3xl font-bold">Invoices</h1>
        <p className="text-muted-foreground">
          Submit invoices for work you've completed. Track approval and payment status.
        </p>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/portal/requests">Requests &amp; bookings</Link>
          </Button>
        </div>
      </header>

      {profileError ? null : profileLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : !hasTaxIdOnFile ? (
        <Card className="p-6 border-amber-500/40 bg-amber-500/5">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
            <div>
              <h2 className="font-semibold">Complete your W-9 to start invoicing</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Before you can submit invoices, we need a W-9 on file for tax reporting.
              </p>
            </div>
            <Button onClick={() => setWizardOpen(true)}>Start W-9</Button>
          </div>
        </Card>
      ) : (
        <Card className="p-6">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
            <div>
              <h2 className="font-semibold">W-9 on file</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Filed under {profile?.legalName}{profile?.businessName ? ` (${profile.businessName})` : ''}.
              </p>
            </div>
            <Button variant="outline" onClick={() => setWizardOpen(true)}>Update W-9</Button>
          </div>
        </Card>
      )}

      {unifiedLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : loadError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm space-y-3">
          <p className="text-destructive">{loadError}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              void loadAssignments()
              void refreshProfile()
              void myInvoices.refresh()
            }}
          >
            Retry
          </Button>
        </div>
      ) : (
        <Tabs defaultValue="engagements">
          <TabsList className="max-w-full overflow-x-auto">
            <TabsTrigger value="engagements">Ready to Invoice</TabsTrigger>
            <TabsTrigger value="history">My Invoices ({myInvoices.data.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="engagements" className="mt-4">
            <EngagementInvoiceList
              assignments={assignments}
              myInvoices={myInvoices.data}
              hasTaxIdOnFile={hasTaxIdOnFile}
              onSubmitInvoice={(engagement) => setDialogTarget({ engagement })}
            />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <MyInvoicesList
              invoices={myInvoices.data}
              loading={myInvoices.loading}
              onResubmit={(invoice) =>
                setDialogTarget({
                  engagement: {
                    id: invoice.engagementId,
                    title: invoice.productionName,
                    clientName:
                      assignments.find((a) => a.id === invoice.engagementId)?.clientName ?? '',
                  },
                  invoice,
                })
              }
            />
          </TabsContent>
        </Tabs>
      )}

      {dialogTarget && memberId && providerMemberId && (
        <SubmitInvoiceDialog
          open={!!dialogTarget}
          onOpenChange={(open) => {
            if (!open) setDialogTarget(null)
          }}
          memberId={memberId}
          providerMemberId={providerMemberId}
          engagement={dialogTarget.engagement}
          invoice={dialogTarget.invoice}
          onSubmitted={() => {
            void myInvoices.refresh()
          }}
        />
      )}

      <W9Wizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        existingProfile={profile}
        onSubmit={async (input) => {
          await taxProfileService.saveProfile(input)
          await refreshProfile()
          setWizardOpen(false)
        }}
      />
    </div>
  )
}
